import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CurrencyDollar, Package, Plus, X } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

const CHARGE_CATEGORIES = ["shipping", "personal_use", "shortage", "other"];

/**
 * v403.4 — Account Charges card (distributor profile).
 * Owner/admin can post debits (amount + reason) against the distributor's
 * account; they get deducted from the next payout. Everyone in scope can see
 * the ledger; only owner/admin can post or void.
 */
export function AccountChargesCard({ distributorId, distributorName, canCharge }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;
  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language === "ru" ? "ru-RU" : "en-US",
      { year: "numeric", month: "short", day: "numeric" }) : "—";

  const load = () => {
    api.get(`/distributors/${distributorId}/charges`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [distributorId]);

  if (!data) return null;
  if (!canCharge && data.items.length === 0) return null;

  const voidCharge = async (c) => {
    if (!window.confirm(t("charges.void_confirm", { amount: fmt$(c.amount) }))) return;
    try {
      await api.post(`/charges/${c.id}/void`);
      toast.success(t("charges.voided_toast"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusPill = (s) => {
    const cls = {
      outstanding: "bg-rose-50 text-rose-700 border-rose-200",
      settled: "bg-emerald-50 text-emerald-700 border-emerald-200",
      voided: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through",
    }[s] || "bg-zinc-100 text-zinc-700 border-zinc-200";
    return (
      <span className={`text-[10px] font-medium px-1.5 py-0.5 border rounded-full ${cls}`}
        data-testid={`charge-status-${s}`}>
        {t(`charges.status_${s}`)}
      </span>
    );
  };

  return (
    <div className="bg-white border-2 border-rose-200 rounded-xl p-4 sm:p-5" data-testid="account-charges-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 flex items-center justify-center">
            <CurrencyDollar size={16} weight="bold" />
          </div>
          <div>
            <h3 className="font-semibold text-base leading-tight">{t("charges.card_title")}</h3>
            {data.outstanding_total > 0 && (
              <p className="text-xs text-rose-700 font-semibold" data-testid="charges-outstanding-total">
                {t("charges.outstanding")}: {fmt$(data.outstanding_total)}
              </p>
            )}
          </div>
        </div>
        {canCharge && (
          <BrutalButton size="sm" onClick={() => setShowDialog(true)} data-testid="charge-account-btn"
            className="!border-rose-300 !text-rose-700 hover:!bg-rose-50" variant="secondary">
            <Plus size={14} weight="bold" /> {t("charges.charge_btn")}
          </BrutalButton>
        )}
      </div>

      {data.items.length === 0 ? (
        <p className="text-xs text-zinc-500 mt-3" data-testid="charges-empty">{t("charges.empty")}</p>
      ) : (
        <div className="mt-3 space-y-2">
          {data.items.map((c) => (
            <div key={c.id}
              className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 rounded-lg border border-zinc-200 px-3 py-2"
              data-testid={`charge-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold tabular-nums text-rose-700">−{fmt$(c.amount)}</span>
                  <span className="text-sm font-medium text-zinc-900 truncate">{c.reason}</span>
                  {c.category && c.category !== "other" && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 border rounded-full bg-sky-50 text-sky-700 border-sky-200"
                      data-testid={`charge-category-${c.id}`}>
                      {t(`charges.cat_${c.category}`)}
                    </span>
                  )}
                  {statusPill(c.status)}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {fmtDate(c.created_at)} · {t("charges.posted_by", { name: c.created_by_name || c.created_by_email })}
                  {c.status === "settled" && c.settled_at && (
                    <> · <span className="text-emerald-700">{t("charges.settled_via")} {fmtDate(c.settled_at)}</span></>
                  )}
                </div>
              </div>
              {canCharge && c.status === "outstanding" && (
                <button onClick={() => voidCharge(c)}
                  className="self-start sm:self-center text-zinc-400 hover:text-rose-600 p-1"
                  title={t("charges.void")} data-testid={`charge-void-${c.id}`}>
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showDialog && (
        <ChargeAccountDialog
          distributorId={distributorId}
          distributorName={distributorName}
          onClose={() => setShowDialog(false)}
          onCreated={() => { setShowDialog(false); load(); }}
        />
      )}
    </div>
  );
}

export function ChargeAccountDialog({
  distributorId, distributorName, onClose, onCreated,
  defaultCategory = "other", defaultReason = "", orderReference = null, storeOrderId = null,
}) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState(defaultReason);
  const [category, setCategory] = useState(defaultCategory);
  const [busy, setBusy] = useState(false);
  // v414.14 — product cross-reference: pick a product, WS price auto-fills.
  const [catalog, setCatalog] = useState(null);
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/distributors/${distributorId}/chargeable-products`)
      .then(({ data }) => setCatalog(data))
      .catch(() => setCatalog(null));
  }, [distributorId]);

  const allProducts = catalog ? [...catalog.held, ...catalog.master] : [];
  const selected = allProducts.find((p) => p.id === productId) || null;

  const applyProduct = (p, q) => {
    if (!p) return;
    const n = Math.max(1, Number(q) || 1);
    if (p.ws != null) setAmount((p.ws * n).toFixed(2));
    setReason(`${t("charges.cat_personal_use")} — ${n}× ${p.name}${p.ws != null ? ` @ $${Number(p.ws).toFixed(2)}` : ""}`);
    if (category === "other") setCategory("personal_use");
  };

  const onPickProduct = (id) => {
    setProductId(id);
    const p = allProducts.find((x) => x.id === id);
    if (p) {
      applyProduct(p, qty);
      if (category !== "personal_use") setCategory("personal_use");
    }
  };
  const onQty = (q) => {
    setQty(q);
    if (selected) applyProduct(selected, q);
  };

  const optLabel = (p) =>
    `${p.name} — ${p.ws != null ? `$${Number(p.ws).toFixed(2)} ws` : t("charges.no_ws")}${p.source === "held" ? ` · ${p.stock} ${t("charges.in_stock")}` : ""}`;

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0 || !reason.trim()) {
      toast.error(t("charges.form_invalid"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/distributors/${distributorId}/charges`, {
        amount: amt, reason: reason.trim(), category,
        order_reference: orderReference, store_order_id: storeOrderId,
        product_id: selected?.id || null,
        product_name: selected?.name || null,
        quantity: selected ? Math.max(1, Number(qty) || 1) : null,
        unit_price: selected?.ws ?? null,
      });
      toast.success(t("charges.created_toast", { amount: `$${amt.toFixed(2)}` }));
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="charge-account-dialog"
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-xl font-semibold tracking-tight">
          {t("charges.dialog_title", { name: distributorName })}
        </h2>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("charges.category")}</label>
          <div className="grid grid-cols-2 gap-2">
            {CHARGE_CATEGORIES.map((cat) => (
              <button key={cat} type="button" onClick={() => setCategory(cat)}
                data-testid={`charge-category-pill-${cat}`}
                className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${
                  category === cat
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}>
                {t(`charges.cat_${cat}`)}
              </button>
            ))}
          </div>
        </div>
        {catalog && allProducts.length > 0 && (
          <div>
            <label className="block text-xs font-semibold mb-1">{t("charges.product_label")}</label>
            <div className="flex gap-2">
              <select
                value={productId}
                onChange={(e) => onPickProduct(e.target.value)}
                className="flex-1 h-10 px-2 rounded-lg border border-zinc-300 bg-white text-sm font-medium focus:outline-none focus:border-[#C9A84C] min-w-0"
                data-testid="charge-product-select"
              >
                <option value="">{t("charges.product_none")}</option>
                {catalog.held.length > 0 && (
                  <optgroup label={t("charges.group_held", { name: distributorName })}>
                    {catalog.held.map((p) => (
                      <option key={p.id} value={p.id}>{optLabel(p)}</option>
                    ))}
                  </optgroup>
                )}
                {catalog.master.length > 0 && (
                  <optgroup label={t("charges.group_master")}>
                    {catalog.master.map((p) => (
                      <option key={p.id} value={p.id}>{optLabel(p)}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {selected && (
                <BrutalInput type="number" min="1" step="1" value={qty}
                  onChange={(e) => onQty(e.target.value)}
                  className="!w-20 text-center" data-testid="charge-qty-input" />
              )}
            </div>
            {selected && selected.ws != null && (
              <p className="text-[11px] text-zinc-500 mt-1" data-testid="charge-ws-math">
                {t("charges.ws_math", {
                  ws: `$${Number(selected.ws).toFixed(2)}`,
                  qty: Math.max(1, Number(qty) || 1),
                  total: `$${(selected.ws * Math.max(1, Number(qty) || 1)).toFixed(2)}`,
                })}
              </p>
            )}
            {selected && selected.ws == null && (
              <p className="text-[11px] text-amber-700 mt-1">{t("charges.no_ws_hint")}</p>
            )}
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold mb-1">{t("charges.amount")}</label>
          <BrutalInput type="number" step="0.01" min="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="305.00" data-testid="charge-amount-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("charges.reason")}</label>
          <BrutalInput value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={category === "shipping" ? t("charges.reason_placeholder_shipping") : t("charges.reason_placeholder")}
            data-testid="charge-reason-input" />
        </div>
        <div className="flex gap-3 pt-2">
          <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="charge-submit">
            {busy ? t("common.saving") : t("charges.charge_btn")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}

/**
 * v407.1 — Shipping costs tally (Settlements page, owner/admin).
 * Running total of shipping charges posted against distributor accounts:
 * outstanding (still to be deducted from payouts) vs already deducted.
 */
export function ShippingCostsCard() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

  useEffect(() => {
    api.get("/charges/summary").then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  }, []);

  if (!summary || !summary.shipping || summary.shipping.total <= 0) return null;
  const s = summary.shipping;

  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="shipping-costs-card">
      <button type="button" onClick={() => setExpanded((v) => !v)}
        className="w-full flex flex-wrap items-center justify-between gap-2 text-left"
        data-testid="shipping-costs-toggle">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 flex items-center justify-center">
            <Package size={16} weight="bold" />
          </div>
          <div>
            <h2 className="font-semibold text-base leading-tight">{t("charges.shipping_card_title")}</h2>
            <p className="text-xs text-zinc-500">{t("charges.shipping_card_hint")}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-lg font-semibold tabular-nums" data-testid="shipping-total">{fmt$(s.total)}</div>
            <div className="text-[11px] text-zinc-500">
              <span className="text-rose-700 font-medium">{fmt$(s.outstanding)} {t("charges.shipping_outstanding")}</span>
              {" · "}
              <span className="text-emerald-700 font-medium">{fmt$(s.settled)} {t("charges.shipping_deducted")}</span>
            </div>
          </div>
        </div>
      </button>
      {expanded && s.by_distributor.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="shipping-by-distributor">
          {s.by_distributor.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-1.5"
              data-testid={`shipping-dist-${d.id}`}>
              <span className="text-sm font-medium text-zinc-800 truncate">{d.name}</span>
              <span className="text-xs text-zinc-500 whitespace-nowrap">
                {t("charges.shipping_n_charges", { n: d.count })} ·{" "}
                <span className="font-semibold tabular-nums text-zinc-900">{fmt$(d.total)}</span>
                {d.outstanding > 0 && (
                  <span className="text-rose-700"> ({fmt$(d.outstanding)} {t("charges.shipping_outstanding")})</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
